import { Formik, FormikHelpers } from "formik";
import React, { useEffect, useState } from "react";
import { Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";

import AuthApi from "../../api/AuthApi";
import { appConstants } from "../../config/app-constants";
import {
  EToastvariant,
  ILoginResponse,
  IVerifyEmailForm,
  IVerifyLoginForm
} from "../../config/interfaces";
import { useAppDispatch } from "../../redux/Store";
import { unsetTempRegisterData } from "../../redux/slice/AuthDataSlice";
import { setLoggedInUser } from "../../redux/slice/LoggedInUserSlice";
import { showToastMsg } from "../../redux/slice/ToastMsgSlice";
import { StorageService, UtilService } from "../../services";
import LongContentModal from "../modals/LongContentModal";
import { ButtonSpinner } from "../placeholders";
import OTPInput from "./OTPInput";

const initialValues: IVerifyLoginForm = {
  otp: ""
};

const validationSchema = Yup.object({
  otp: Yup.string().min(6, "OTP is required").max(6, "OTP is required").required("OTP is required")
});

const VerificationForm: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useLocation();

  const dispatch = useAppDispatch();

  const [localPreVerifyData, setLocalPreVerifyData] = useState<any>(null);
  const [maskedEmail, setMaskedEmail] = useState<string | null>(null);
  const [enableOtpLink, setEnableOtpLink] = useState<boolean>(true);
  const [countdown, setCountdown] = useState<number>(appConstants.resendOtpTime);
  const [isOTPSending, setIsOTPSending] = useState<boolean>(false);
  const [showTermsOfServiceModal, setShowTermsOfServiceModal] = useState<boolean>(false);
  const [isAccepting, setIsAccepting] = useState<boolean>(false);

  useEffect(() => {
    if (!state?.showVerification) {
      navigate("/login", { replace: true });
    }
  }, []);

  useEffect(() => {
    const getLocalData = async () => {
      const _preVerifyData = await StorageService.getPreVerifyData();
      if (_preVerifyData) {
        setLocalPreVerifyData(() => JSON.parse(_preVerifyData));
      } else {
        navigate("/login", { replace: true });
      }
    };

    getLocalData();
  }, []);

  useEffect(() => {
    if (localPreVerifyData && localPreVerifyData?.email) {
      const [username, domain] = localPreVerifyData.email.split("@");
      const maskedUsername =
        username.length > 4
          ? username.slice(0, 4) + "*".repeat(username.length - 4)
          : username.substring(0, 1) + "*".repeat(username.length - 1);
      setMaskedEmail(`${maskedUsername}@${domain}`);
    }
  }, [localPreVerifyData?.email]);

  // used for timer
  useEffect(() => {
    let timer: any = null;

    if (enableOtpLink) {
      // If the link is disabled, start the countdown
      // Update the countdown every second
      timer = setInterval(() => {
        setCountdown((prevCountdown) => prevCountdown - 1);
      }, 1000);

      // Enable the link after 60 seconds
      setTimeout(() => {
        setEnableOtpLink(false);
        clearInterval(timer);
      }, appConstants.resendOtpTime * 1000);
    }

    return () => clearInterval(timer);
  }, [enableOtpLink]);

  const handleVerifyFormSubmit = async (
    values: IVerifyLoginForm,
    { setSubmitting }: FormikHelpers<IVerifyLoginForm>
  ) => {
    try {
      setSubmitting(true);

      if (localPreVerifyData) {
        const _values: IVerifyEmailForm = {
          email: localPreVerifyData.email,
          otp: values.otp,
          preVerificationToken: localPreVerifyData.preVerificationToken
        };
        const authApi = AuthApi.getInstance();
        const verifyResp = await authApi.verifyEmail(_values);

        await StorageService.setPostVerifyData(
          JSON.stringify({
            email: _values.email,
            postVerificationToken: verifyResp.postVerificationToken
          })
        );

        if (state?.fromScreen === "forgot-password") {
          navigate("/reset-password");
          return;
        }

        const loginResp = await authApi.loginByPostVerificationToken({
          postVerificationToken: verifyResp.postVerificationToken
        });

        await StorageService.setApiToken(loginResp.authToken);

        if (state?.fromScreen === "register") {
          dispatch(unsetTempRegisterData());
          handleAuth(loginResp, "/register-successful");
          dispatch(
            showToastMsg({
              isVisible: true,
              title: "Success",
              message: "Register successfully",
              variant: EToastvariant.Success
            })
          );
          return;
        }

        dispatch(
          showToastMsg({
            isVisible: true,
            title: "Success",
            message: "Login successfully",
            variant: EToastvariant.Success
          })
        );

        // TODO: what will happen if terms is not accepted
        handleAuth(loginResp);
        // const is_terms_accepted: boolean = !!loginResp.user.termsAcceptedAt;
        // if (is_terms_accepted) {
        // 	handleAuth(loginResp);
        // } else {
        // 	setShowTermsOfServiceModal(true);
        // }
      }
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendOTP = async () => {
    try {
      setIsOTPSending(true);

      const authApi = AuthApi.getInstance();
      const sendOtpResp = await authApi.sendOTP({
        email: localPreVerifyData.email
      });

      await StorageService.setPreVerifyData(
        JSON.stringify({
          email: localPreVerifyData.email,
          preVerificationToken: sendOtpResp.preVerificationToken
        })
      );
      //  Update localPreVerifyData state
      setLocalPreVerifyData((prevState: any) => ({
        ...prevState,
        preVerificationToken: sendOtpResp.preVerificationToken
      }));

      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Success",
          message: "OTP resent successfully",
          variant: EToastvariant.Success
        })
      );
      setEnableOtpLink(true);
      setCountdown(appConstants.resendOtpTime);
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    } finally {
      setIsOTPSending(false);
    }
  };

  const handleAcceptTermsService = async () => {
    try {
      setIsAccepting(true);
      const authApi = AuthApi.getInstance();

      const loginResp = await authApi.acceptTermsService();
      handleAuth(loginResp.data.data);
      setShowTermsOfServiceModal(false);
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    } finally {
      setIsAccepting(false);
      setShowTermsOfServiceModal(false);
    }
  };

  const handleAuth = async (_data: ILoginResponse, route?: string) => {
    await StorageService.setTermsAccepted(_data.user.termsAcceptedAt);
    await StorageService.setUserDeviceToken(_data.deviceToken);
    await StorageService.removePrePostVerifyData();

    dispatch(setLoggedInUser(_data.user));

    // check it came from marketing plans screen
    const isPublicPlanUUID = await StorageService.getMarketPlansUrl();
    if (isPublicPlanUUID) {
      navigate("/itr-filings", { replace: true });
    } else if (route) {
      navigate(route, { replace: true });
    } else {
      navigate("/home", { replace: true });
    }
  };

  return (
    <>
      <Container>
        <div className="auth-form-wrap otp-form-wrap">
          <Row className="align-items-center justify-content-md-center">
            <Col>
              <Card bg="white" className="auth-card">
                <Card.Body>
                  <h1 className="fw-bold text-center mb-1">Please verify your account</h1>
                  <p className="text-center text-greyDark">
                    Enter the 6 digit code sent to your email
                  </p>
                  <p className="text-center text-primary">{maskedEmail}</p>

                  <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleVerifyFormSubmit}
                  >
                    {({
                      handleBlur,
                      handleSubmit,
                      setFieldValue,
                      values,
                      errors,
                      touched,
                      isSubmitting
                    }) => (
                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          handleSubmit();
                        }}
                      >
                        {/* OTP fields */}
                        <div className="otp-input-wrap">
                          <OTPInput
                            value={values.otp}
                            otpLength={6}
                            onChange={(e) => {
                              setFieldValue("otp", e);
                            }}
                            error={errors.otp}
                            touched={Boolean(touched.otp)}
                            handleBlur={handleBlur}
                          />
                        </div>

                        {/* Verify button */}
                        <div className="btn-wrap">
                          <Button
                            variant="primary"
                            type="submit"
                            size="lg"
                            className="w-100 shadow"
                            disabled={isSubmitting}
                          >
                            {!isSubmitting ? (
                              state?.fromScreen === "forgot-password" ? (
                                "Continue"
                              ) : (
                                "Verify"
                              )
                            ) : (
                              <ButtonSpinner />
                            )}
                          </Button>
                        </div>

                        <div className="d-flex align-items-center justify-content-center">
                          {enableOtpLink ? (
                            <Button
                              variant="link"
                              size="sm"
                              className="pe-none text-decoration-none"
                            >
                              <span className="text-black text-decoration-underline">
                                &#10088;{String(countdown).padStart(2, "0")}
                                s&#10089;
                              </span>

                              <span className="text-greyDark text-decoration-underline">
                                &nbsp;Resend code
                              </span>
                            </Button>
                          ) : (
                            <Button variant="link" size="sm" onClick={handleResendOTP}>
                              {!isOTPSending ? "Resend Code" : <ButtonSpinner />}
                            </Button>
                          )}
                        </div>
                      </Form>
                    )}
                  </Formik>
                </Card.Body>
              </Card>

              <div className="auth-footer-1 justify-content-end">
                <Form.Text className="m-0 text-greyDark">Back to</Form.Text>
                <Button
                  variant="outline-greyDark"
                  size="sm"
                  onClick={() => {
                    dispatch(unsetTempRegisterData());
                    navigate("/login");
                  }}
                >
                  Sign In
                </Button>
              </div>
            </Col>
          </Row>
        </div>
      </Container>

      <LongContentModal
        showModal={showTermsOfServiceModal}
        title="Terms of Service"
        onYesBtnText="Close"
        onYesBtnClick={() => {
          handleAcceptTermsService();
        }}
        isProcessing={isAccepting}
        actionKey={"termsOfService"}
      />
    </>
  );
};

export default VerificationForm;
